/**
 * APP.JSX - Composant principal de l'application
 * 
 * Ce composant configure le Canvas React Three Fiber et initialise la scène 3D.
 * Il gère la configuration de la caméra, les paramètres de rendu et le chargement.
 * 
 * RESPONSABILITÉS :
 * - Configuration du Canvas R3F
 * - Configuration de la caméra
 * - Gestion du Suspense pour le chargement
 * - Import de la scène 3D principale
 * - Providers pour ressources et audio
 * - Préchargement des ressources
 * - Interface de debug (dev uniquement)
 */

import { Canvas } from '@react-three/fiber'
import { Suspense, useState, useCallback } from 'react'
import { Leva } from 'leva'
import Scene from './components/Scene'
import { AudioProvider } from './providers/AudioProvider'
import { ResourcesProvider } from './providers/ResourcesProvider'
import { AudioDebug } from './components/AudioDebug'
import { KetchupLoader } from './components/KetchupLoader'
import { LoaderTracker } from './components/LoaderTracker'
import BackgroundSelector from './components/BackgroundSelector'
import { preloadResources } from './hooks/useResources'
import { useDebugMode } from './hooks/useDebugMode'
import { getDefaultBackground } from './config/backgrounds'

// ============================================
// PRÉCHARGEMENT DES RESSOURCES
// ============================================
// Lancer le préchargement AVANT le premier rendu
// Permet d'éviter les "flash" de chargement
preloadResources()

function App() {
  // Détecte si l'URL contient #debug pour afficher Leva
  const isDebugMode = useDebugMode()
  
  // État pour la progression du chargement (mis à jour par LoaderTracker)
  const [loadingProgress, setLoadingProgress] = useState(0)
  
  // État pour contrôler quand l'utilisateur a cliqué sur "Go!"
  const [hasStarted, setHasStarted] = useState(false)
  
  // État pour le background actuel (contrôlé par BackgroundSelector)
  const [currentBackground, setCurrentBackground] = useState(getDefaultBackground())
  
  // Callback pour recevoir la progression depuis le Canvas
  const handleProgress = useCallback((progress) => {
    setLoadingProgress(progress)
  }, [])
  
  // Callback quand l'utilisateur clique sur "Go!"
  const handleStart = useCallback(() => {
    setHasStarted(true)
  }, [])

  return (
    <>
      {/* ============================================ */}
      {/* LOADER KETCHUP (HORS DU CANVAS) */}
      {/* ============================================ */}
      {/* 
        Le loader est placé en dehors du Canvas car c'est du HTML/CSS.
        Il reçoit la progression depuis LoaderTracker (dans le Canvas).
        Il se cache automatiquement quand l'utilisateur clique sur "Go!".
      */}
      {!hasStarted && (
        <KetchupLoader 
          progress={loadingProgress} 
          onStart={handleStart}
        />
      )}

      {/* ============================================ */}
      {/* CONTRÔLE GLOBAL DE LEVA */}
      {/* ============================================ */}
      {/* 
        Masque ou affiche TOUS les panneaux Leva selon l'URL :
        - Sans #debug : hidden={true} → Leva masqué (production)
        - Avec #debug : hidden={false} → Leva visible (debug)
        
        Le composant <Leva /> contrôle l'affichage global de tous les
        panneaux créés avec useControls() partout dans l'application.
      */}
      <Leva hidden={!isDebugMode} />

      {/* ============================================ */}
      {/* INTERFACE DE DEBUG (DEV + #debug) */}
      {/* ============================================ */}
      {/* 
        AudioDebug visible seulement si :
        - En mode développement (import.meta.env.DEV)
        - ET avec #debug dans l'URL
      */}
      {import.meta.env.DEV && isDebugMode && <AudioDebug />}

      {/* ============================================ */}
      {/* PROVIDERS GLOBAUX */}
      {/* ============================================ */}
      <AudioProvider>
        <ResourcesProvider>
          {/* ============================================ */}
          {/* CANVAS R3F */}
          {/* ============================================ */}
          <Canvas
            // Configuration de la caméra (identique au code vanilla CameraOrbit.js)
            camera={{
              position: [10.8, -13.8, 11.0],  // Position initiale
              fov: 40,                         // Field of View
              near: 1,                         // Near plane
              far: 80,                         // Far plane
              up: [0, 0, 1]                    // Z-up (important pour correspondre au vanilla)
            }}
            // Configuration du rendu WebGL
            gl={{
              antialias: true,
              alpha: false
            }}
            // Activation des ombres (shadow mapping)
            shadows
            // Style du canvas (fullscreen)
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%'
            }}
          >
            {/* Tracker de progression (dans le Canvas) */}
            <LoaderTracker onProgress={handleProgress} />
            
            {/* Suspense pour gérer le chargement asynchrone */}
            <Suspense fallback={null}>
              <Scene background={currentBackground} />
            </Suspense>
          </Canvas>

          {/* ============================================ */}
          {/* SÉLECTEUR DE BACKGROUND (HORS DU CANVAS) */}
          {/* ============================================ */}
          {/* 
            Bouton en bas à droite pour changer les couleurs du Floor.
            Visible seulement après le démarrage (quand le loader a disparu).
          */}
          {hasStarted && (
            <BackgroundSelector
              onBackgroundChange={setCurrentBackground}
              activeId={currentBackground.id}
            />
          )}
        </ResourcesProvider>
      </AudioProvider>
    </>
  )
}

export default App

